// Angular imports
import { Component, OnInit, EventEmitter, Input, Output, ViewChild } from '@angular/core';

// Component imports
import { DxDataGridComponent } from 'devextreme-angular';
import { custom } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';

// CC-Common imports
import { IconConstants } from 'cc-common';

// Service imports
import { DetailPopupsService } from '../../_services/detail-popups.service';
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { CashReceiptService } from '../../_services/cash-management/cash-receipt.service';

// Constant imports
import { MessageInterval } from '../../../../_constants/messages-intervals';
import { StringFormatConstants } from '../../../../_constants/string-format-constants';

// Model imports
import { CashReceiptItemResultDto } from '../../_models/cash-management/cash-receipts/cash-receipt-item-result-dto';
import { CashReceiptParametersDto } from '../../_models/cash-management/cash-receipts/cash-receipt-parameters-dto';
import { CashReceiptDetailResultDto } from '../../_models/cash-management/cash-receipts/cash-receipt-detail-result-dto';

@Component({
    selector: 'app-cash-receipt-details',
    templateUrl: './cash-receipt-details.component.html',
    styleUrls: ['./cash-receipt-details.component.scss']
})
export class CashReceiptDetailsComponent implements OnInit {
    @ViewChild('cashReceiptRawGrid', { static: false }) dataGrid: DxDataGridComponent;

    @Input() visible = false;
    @Input() cashReceiptParams: CashReceiptParametersDto;
    @Output() onCashReceiptClose = new EventEmitter<boolean>();

    iconClose = IconConstants.CLOSE;
    iconReverse = IconConstants.REVERSE;
    iconDelete = IconConstants.DELETE;
    iconEdit = IconConstants.EDIT;
    dateFormat = StringFormatConstants.dateFormat;

    loadingVisible = false;
    title: string;
    editorTitle: string;
    cashReceiptResult: CashReceiptDetailResultDto;
    cashReceiptLineItems: CashReceiptItemResultDto[];

    isEditAllowed = false;
    isLimitedEditAllowed = false;

    constructor(
        private readonly detailsService: DetailPopupsService,
        private readonly errorService: ServerErrorService,
        private readonly cashReceiptService: CashReceiptService
    ) {}

    ngOnInit() {
        if (!!this.cashReceiptParams) {
            this.title = `Cash Receipt# ${this.cashReceiptParams.id}`;
            this.editorTitle = `Update Cash Receipt# ${this.cashReceiptParams.id}`;
            this.populateCashReceiptDetails();
        }
    }

    onHidden(): void {
        this.visible = false;
        this.onCashReceiptClose.emit();
    }

    getDimensionOptionTitle(record): string {
        let title: string = null;
        const data: any = record.data;
        const column: any = record.column;
        const dimesion = data.dimensions.find((rec) => rec.dimensionCode === column.dataField);
        if (!!dimesion) {
            title = dimesion.dimensionOptionTitle;
        }

        return title;
    }

    onDeleteClick(): void {
        const customDialog = custom({
            title: 'Delete Cash Receipt',
            messageHtml: `<b> Are you sure to delete cash receipt #${this.cashReceiptParams.id}?</b>`,
            buttons: [
                {
                    type: 'danger',
                    onClick: (e) => {
                        this.deleteCashReceipt();
                    }
                },
                {
                    type: 'default',
                    onClick: (e) => {
                        return;
                    }
                }
            ]
        });
        customDialog.show();
    }

    onReverseClick(): void {
        this.detailsService.updateSubjectCashReceiptReverseEditor(this.cashReceiptResult, this.cashReceiptParams);
        this.onHidden();
    }

    onEditClick(): void {
        this.detailsService.updateSubjectCashReceiptEditor(
            this.editorTitle,
            this.cashReceiptResult,
            this.cashReceiptParams
        );
        this.onHidden();
    }

    private populateCashReceiptDetails(): void {
        this.loadingVisible = true;
        this.cashReceiptService.getCashReceiptDetails(this.cashReceiptParams).subscribe(
            (result: CashReceiptDetailResultDto) => {
                this.cashReceiptResult = result;
                this.processCashReceipt();

                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private processCashReceipt(): void {
        this.isLimitedEditAllowed = this.cashReceiptResult.allowLimitedEdit;
        this.isEditAllowed = this.cashReceiptResult.allowEdit;

        this.cashReceiptLineItems = this.cashReceiptResult.items;
        this.updateDimensionColumns();
    }

    private deleteCashReceipt(): void {
        this.loadingVisible = true;
        this.cashReceiptService.deleteCashReceipt(this.cashReceiptParams).subscribe(
            (response: string) => {
                notify(response, 'Success', MessageInterval.successMsgInterval);
                this.onHidden();
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private updateDimensionColumns(): void {
        const newColumns: any[] = [];
        this.cashReceiptLineItems.forEach((item) => {
            const dimensions = item.dimensions;
            dimensions.forEach((dim) => {
                if (!newColumns.find((d) => d.dataField === dim.dimensionCode)) {
                    newColumns.push({
                        dataField: dim.dimensionCode,
                        caption: dim.dimensionTitle,
                        dataType: 'string',
                        cellTemplate: 'dimensionTemplate'
                    });
                }
            });
        });

        if (!!this.dataGrid && !!this.dataGrid.instance) {
            const gridColumns = this.dataGrid.instance.getVisibleColumns();
            newColumns.forEach((col) => {
                if (!gridColumns.find((d) => d.dataField === col.dataField)) {
                    this.dataGrid.instance.addColumn(col);
                }
            });
            this.dataGrid.instance.repaint();
        }
    }
}
